/**
 * MeuMunicipio — Funcionalidade 2 (Fase 2)
 *
 * Public page /meu-municipio: user picks state + city, sees a summary of
 * public spending (emendas, convênios, transferências) with graceful
 * fallback when the Portal da Transparência API key is not configured.
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import ExplainButton from "../components/ExplainButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB",
  "PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const brl = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

const toSlug = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function MeuMunicipio() {
  const [uf, setUf] = useState("PE");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const searchMunis = useCallback(async (nextUf, nextQ) => {
    if (!nextQ || nextQ.length < 2) { setSuggestions([]); return; }
    try {
      const r = await fetch(`${BACKEND}/api/public/ibge/municipios?uf=${nextUf}&q=${encodeURIComponent(nextQ)}`);
      const j = await r.json();
      setSuggestions(j.items || []);
    } catch { setSuggestions([]); }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => searchMunis(uf, query), 200);
    return () => clearTimeout(id);
  }, [uf, query, searchMunis]);

  const loadMuni = async (nome) => {
    setLoading(true); setErr(null); setResult(null); setSuggestions([]);
    try {
      const r = await fetch(`${BACKEND}/api/public/municipio/${uf}/${toSlug(nome)}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "network");
      const j = await r.json();
      setResult(j);
      setQuery(j.municipio.nome);
    } catch (e) {
      setErr(e.message === "Município não encontrado" ? "not_found" : "network");
    } finally {
      setLoading(false);
    }
  };

  const shareWhatsApp = () => {
    if (!result) return;
    const m = result.municipio;
    const emenda = result.emendas.unavailable
      ? "Dados de emendas em preparação."
      : `R$ ${(result.emendas.total || 0).toLocaleString("pt-BR")} em emendas parlamentares.`;
    const site = typeof window !== "undefined" ? window.location.origin : BACKEND;
    const text = `🏙️ ${m.nome} (${m.uf}) — ${emenda}\nSaiba mais no Tô de Olho:\n${site}/meu-municipio`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="meu-municipio-page">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-[#006030] mb-2">Radar do Meu Município</p>
        <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-1">Para onde vai o dinheiro na sua cidade?</h1>
        <p className="text-sm text-slate-600">Consulte emendas parlamentares, convênios e repasses federais recebidos pelo seu município.</p>
      </header>

      <section className="bg-white rounded-2xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">Estado</label>
            <select
              value={uf}
              onChange={(e) => setUf(e.target.value)}
              className="w-full px-3 py-3 border border-slate-300 rounded-lg min-h-[44px] focus:outline-none focus:ring-2 focus:ring-[#006030]/30"
              data-testid="muni-uf-select"
            >
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="col-span-2 relative">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">Cidade</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Digite o nome da cidade…"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg min-h-[44px] focus:outline-none focus:ring-2 focus:ring-[#006030]/30"
              data-testid="muni-city-input"
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-10 bg-white border border-slate-200 rounded-lg mt-1 w-full max-h-56 overflow-y-auto shadow-lg">
                {suggestions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => loadMuni(s.nome)}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm text-slate-700"
                      data-testid={`muni-suggestion-${s.id}`}
                    >
                      {s.nome}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {loading && (
        <div className="text-center py-10 text-slate-600" data-testid="muni-loading">Consultando dados oficiais…</div>
      )}

      {err && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-testid="muni-error">
          {err === "not_found" ? "Não encontramos essa cidade. Tente escrever de novo." : "Ops! O serviço está indisponível agora. Tente em alguns minutos."}
        </div>
      )}

      {result && !loading && (
        <>
          <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" data-testid="muni-result">
            <div className="bg-gradient-to-br from-[#006030] to-[#004a24] p-6 text-white">
              <p className="text-xs uppercase tracking-widest text-white/70 mb-1">{result.municipio.uf}</p>
              <h2 className="text-3xl font-black">{result.municipio.nome}</h2>
              <p className="text-xs text-white/80 mt-2">Código IBGE: {result.municipio.codigo_ibge}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
              {/* Emendas */}
              <div className="p-6 text-center" data-testid="muni-card-emendas">
                <p className="text-3xl mb-2" aria-hidden="true">💰</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Emendas recebidas</p>
                {result.emendas.unavailable ? (
                  <p className="text-xs text-amber-700">{result.emendas.message}</p>
                ) : (
                  <>
                    <p className="text-2xl font-black text-[#006030]">{brl(result.emendas.total)}</p>
                    <p className="text-xs text-slate-500 mt-1">{result.emendas.quantidade} emendas</p>
                    <div className="mt-2 flex justify-center">
                      <ExplainButton
                        contexto={`total de emendas parlamentares recebidas por ${result.municipio.nome}`}
                        valor={brl(result.emendas.total)}
                        tipo="valor"
                        fonte="Portal da Transparência"
                        entidade={`${result.municipio.nome} / ${result.municipio.uf}`}
                        testId="explain-emendas-total"
                      />
                    </div>
                  </>
                )}
              </div>
              {/* Convênios */}
              <div className="p-6 text-center" data-testid="muni-card-convenios">
                <p className="text-3xl mb-2" aria-hidden="true">🤝</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Convênios ativos</p>
                {result.convenios.unavailable ? (
                  <p className="text-xs text-slate-500">Dados em preparação</p>
                ) : (
                  <p className="text-2xl font-black text-slate-800">{result.convenios.quantidade}</p>
                )}
              </div>
              {/* Transferências */}
              <div className="p-6 text-center" data-testid="muni-card-transferencias">
                <p className="text-3xl mb-2" aria-hidden="true">📊</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Repasses federais ({result.transferencias.ano})</p>
                {result.transferencias.unavailable ? (
                  <p className="text-xs text-slate-500">Dados em preparação</p>
                ) : (
                  <>
                    <p className="text-2xl font-black text-slate-800">{brl(result.transferencias.total_ano)}</p>
                    <div className="mt-2 flex justify-center">
                      <ExplainButton
                        contexto={`total de repasses federais recebidos por ${result.municipio.nome} em ${result.transferencias.ano}`}
                        valor={brl(result.transferencias.total_ano)}
                        tipo="valor"
                        fonte="Portal da Transparência"
                        entidade={`${result.municipio.nome} / ${result.municipio.uf}`}
                        testId="explain-transferencias-total"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {result.emendas.top_parlamentares && result.emendas.top_parlamentares.length > 0 && (
              <div className="border-t border-slate-100 p-6" data-testid="muni-top-parlamentares">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-3 font-semibold">Quem mandou dinheiro para cá</p>
                <ul className="space-y-2">
                  {result.emendas.top_parlamentares.map((p, i) => (
                    <li key={i} className="flex justify-between text-sm py-1 border-b border-slate-50">
                      <span className="text-slate-800">{p.nome}</span>
                      <span className="font-semibold text-[#006030]">{brl(p.valor)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <button
            type="button"
            onClick={shareWhatsApp}
            className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-3 rounded-lg min-h-[44px] transition-colors mb-4"
            data-testid="muni-share-whatsapp"
          >
            Compartilhar no WhatsApp
          </button>
        </>
      )}
    </div>
  );
}
